import Footer from '../../../components/Footer/Footer';
import InnerNav from '../../../components/InnerNav/InnerNav';
import Zodiac from '../../../components/ZodiacSign/Zodiac';
import './LuckyNumber.scss';
import arrow from '../../../assets/arrow.png';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const reduceToDigit = (num) => {
  while (num > 9 && ![11, 22, 33].includes(num)) {
    num = num
      .toString()
      .split('')
      .reduce((a, b) => a + Number(b), 0);
  }
  return num;
};

const luckyDatesMap = {
  1: [1, 10, 19, 28],
  2: [2, 11, 20, 29],
  3: [3, 12, 21, 30],
  4: [4, 13, 22, 31],
  5: [5, 14, 23],
  6: [6, 15, 24],
  7: [7, 16, 25],
  8: [8, 17, 26],
  9: [9, 18, 27],
};

// Detailed numerology profiles for each number
export const luckyNumberProfiles = {
  1: {
    title: "Number 1 – The Leader",
    keywords: "Leadership · Independence · Initiative",
    overview:
      "Number 1 is associated with originality, courage and self-reliance. It represents the energy of new beginnings and the power to take initiative.",
    strengths: [
      "Strong leadership qualities and decision-making ability",
      "High ambition and desire to achieve",
      "Natural confidence and self-motivation",
      "Able to start and drive projects forward",
    ],
    challenges: [
      "Can sometimes appear dominating or stubborn",
      "Impatience when others are slow or indecisive",
      "May struggle with taking advice or working under authority",
    ],
    guidance:
      "Use your leadership power to inspire, not control. When you balance confidence with humility, Number 1 becomes extremely successful and respected."
  },
  2: {
    title: "Number 2 – The Harmoniser",
    keywords: "Balance · Cooperation · Sensitivity",
    overview:
      "Number 2 is linked with diplomacy, peace and emotional intelligence. It supports partnerships, understanding and creating harmony around you.",
    strengths: [
      "Excellent mediator and team player",
      "Highly intuitive and emotionally aware",
      "Sensitive to the needs and feelings of others",
      "Good at building long-term relationships",
    ],
    challenges: [
      "Can become over-sensitive or easily hurt",
      "Tendency to avoid conflict even when necessary",
      "May depend too much on others for validation",
    ],
    guidance:
      "Your gift is harmony. When you set healthy boundaries and speak your truth calmly, you become a powerful stabilising presence for others."
  },
  3: {
    title: "Number 3 – The Communicator",
    keywords: "Creativity · Expression · Optimism",
    overview:
      "Number 3 symbolises creativity, joy and self-expression. It governs artistic abilities, communication skills and a naturally positive outlook.",
    strengths: [
      "Strong communication and presentation skills",
      "Creative, imaginative and expressive",
      "Brings joy, lightness and humour to situations",
      "Naturally attracts people and opportunities",
    ],
    challenges: [
      "Difficulty with routine and discipline",
      "May scatter energy over too many ideas",
      "Can be overly talkative or superficial at times",
    ],
    guidance:
      "Channel your creativity into focused goals. When you combine discipline with your natural talent, Number 3 can achieve remarkable success."
  },
  4: {
    title: "Number 4 – The Builder",
    keywords: "Stability · Discipline · Structure",
    overview:
      "Number 4 is the number of foundations, order and responsibility. It supports long-term planning and practical, steady progress.",
    strengths: [
      "Reliable, organised and hardworking",
      "Excellent at planning and managing details",
      "Strong sense of duty and responsibility",
      "Good at building stable careers and systems",
    ],
    challenges: [
      "Can become rigid or resistant to change",
      "May focus too much on work and neglect joy",
      "Tendency to be overly critical of self and others",
    ],
    guidance:
      "Your strength is stability. When you allow some flexibility and openness to new ideas, you create structures that are both strong and adaptable."
  },
  5: {
    title: "Number 5 – The Explorer",
    keywords: "Freedom · Change · Adventure",
    overview:
      "Number 5 represents movement, adaptability and curiosity. It brings variety, learning through experience and a desire for freedom.",
    strengths: [
      "Highly adaptable and open to change",
      "Quick learner with versatile skills",
      "Enjoys meeting new people and exploring ideas",
      "Can handle fast-paced environments well",
    ],
    challenges: [
      "May become restless or easily bored",
      "Struggles with routine, commitment and consistency",
      "Risk of impulsive decisions or scattered focus",
    ],
    guidance:
      "Embrace your love of change, but anchor it with clear priorities. When your freedom is guided by purpose, Number 5 becomes very successful."
  },
  6: {
    title: "Number 6 – The Nurturer",
    keywords: "Responsibility · Care · Harmony",
    overview:
      "Number 6 is linked to care, protection and service. It emphasises family, relationships and creating a balanced, beautiful environment.",
    strengths: [
      "Deeply caring and supportive",
      "Strong sense of responsibility towards loved ones",
      "Good aesthetic sense and love for beauty",
      "Excellent at creating warmth and comfort at home",
    ],
    challenges: [
      "May take on too many responsibilities",
      "Tendency to worry or be overprotective",
      "Can feel hurt when efforts are not appreciated",
    ],
    guidance:
      "Your strength lies in your heart. When you also care for yourself and set healthy limits, Number 6 attracts lasting love and respect."
  },
  7: {
    title: "Number 7 – The Seeker",
    keywords: "Wisdom · Intuition · Analysis",
    overview:
      "Number 7 is associated with introspection, research and spiritual insight. It searches for deeper truths beyond the surface.",
    strengths: [
      "Analytical, thoughtful and observant",
      "Strong intuition and spiritual inclination",
      "Enjoys study, research and specialised knowledge",
      "Can remain calm and detached in complex situations",
    ],
    challenges: [
      "May become withdrawn or emotionally distant",
      "Tendency to overthink or doubt",
      "Can struggle to express feelings directly",
    ],
    guidance:
      "Trust both your mind and intuition. When you share your insights with others, Number 7 becomes a respected guide and advisor."
  },
  8: {
    title: "Number 8 – The Achiever",
    keywords: "Success · Power · Management",
    overview:
      "Number 8 relates to material success, authority and executive ability. It is strongly linked with business, finance and long-term achievement.",
    strengths: [
      "Strong management and leadership skills",
      "Good sense for business and strategy",
      "Persistent, determined and goal-oriented",
      "Capacity to handle responsibility and large projects",
    ],
    challenges: [
      "Risk of workaholism or extreme ambition",
      "May seem strict or unemotional at times",
      "Karmic lessons around power, control and fairness",
    ],
    guidance:
      "Use your power fairly and responsibly. When you align ambition with integrity, Number 8 attracts sustainable success and respect."
  },
  9: {
    title: "Number 9 – The Humanitarian",
    keywords: "Compassion · Creativity · Completion",
    overview:
      "Number 9 is linked with compassion, idealism and emotional depth. It carries the energy of completion and universal love.",
    strengths: [
      "Highly empathetic and compassionate",
      "Creative, imaginative and emotionally expressive",
      "Strong desire to help and uplift others",
      "Able to see the bigger picture beyond personal gain",
    ],
    challenges: [
      "Can absorb others’ emotional burdens",
      "Tendency towards moodiness or emotional swings",
      "May struggle with letting go of the past",
    ],
    guidance:
      "Your heart is your strength. When you balance caring for others with caring for yourself, Number 9 becomes a powerful force for positive change."
  },
};

const LuckyNum = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    year: '2000',
    month: 'May',
    day: '26',
    gender: 'male',
  });

  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { firstName, lastName, year, month, day, gender } = formData;
    const birthDay = Number(day);
    const birthMonth = new Date(`${month} 1, 2000`).getMonth() + 1;
    const birthYear = Number(year);

    try {
      setLoading(true);
      const res = await axios.get(
        `https://core-c343.onrender.com/api/lucky-number` +
          `?firstName=${firstName}` +
          `&lastName=${lastName}` +
          `&birthYear=${birthYear}` +
          `&birthMonth=${birthMonth}` +
          `&birthDay=${birthDay}` +
          `&gender=${gender}`
      );
      setResultData(res.data);
      setShowReport(true);
    } catch {
      setResultData({ error: 'Unable to generate your report.' });
      setShowReport(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setShowReport(false);
    setResultData(null);
  };

  const compoundLucky = resultData?.data?.numerology?.luckyNumber;
  const luckyRoot = compoundLucky ? reduceToDigit(compoundLucky) : null;
  const profile = luckyRoot ? luckyNumberProfiles[luckyRoot] : null;

  return (
    <div className="lucky-container">
      <InnerNav />

      <div className="lucky-content">
        <Zodiac />

        <div className="left-numerology">

          {/* ---------------- FORM VIEW ---------------- */}
          {!showReport && (
            <>
              <div className="header">
                <h1>Lucky Number Calculator</h1>
                <p>
                  Enter your details below to receive a complete numerology analysis of your <strong>Lucky Number</strong>.
                </p>
              </div>

              <div className="form-card">
                <form onSubmit={handleSubmit} className="form-grid">

                  <input name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required />
                  <input name="middleName" placeholder="Middle Name (Optional)" value={formData.middleName} onChange={handleChange} />
                  <input name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} required />

                  <div className="dob-grid">
                    <select name="day" value={formData.day} onChange={handleChange}>
                      {[...Array(31)].map((_, i) => <option key={i + 1}>{i + 1}</option>)}
                    </select>

                    <select name="month" value={formData.month} onChange={handleChange}>
                      {[
                        'January','February','March','April','May','June',
                        'July','August','September','October','November','December'
                      ].map((m) => <option key={m}>{m}</option>)}
                    </select>

                    <select name="year" value={formData.year} onChange={handleChange}>
                      {Array.from({ length: 110 }).map((_, i) => {
                        const y = 2025 - i;
                        return <option key={y}>{y}</option>;
                      })}
                    </select>
                  </div>
                  <select
  name="gender"
  value={formData.gender}
  onChange={handleChange}
  required
>
  <option value="male">Male</option>
  <option value="female">Female</option>
  <option value="other">Other</option>
</select>


                  <button type="submit" className="primary-btn" disabled={loading}>
                    {loading ? "Generating..." : "Show My Lucky Number"}
                  </button>

                </form>
              </div>
            </>
          )}

          {/* ---------------- REPORT VIEW ---------------- */}
          {showReport && (
            <div className="report-card">

              <h1>Your Lucky Number Report</h1>

              {resultData?.error ? (
                <p className="error-text">{resultData.error}</p>
              ) : (
                <>
                  <div className="hero-number-box">
                    {/* <div className="number-circle">{luckyNum}</div> */}
                    <div className="number-circle">{luckyRoot}</div>

                    <h2>{profile?.title}</h2>
                    <p className="keywords">{profile?.keywords}</p>
                  </div>

                  <div className="report-section">
                    <h3>Overview</h3>
                    <p>{profile?.overview}</p>
                  </div>

                  <div className="report-section">
                    <h3>Strengths</h3>
                    <ul>{profile?.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>

                  <div className="report-section">
                    <h3>Challenges</h3>
                    <ul>{profile?.challenges.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  </div>

                  <div className="report-section">
                    <h3>Guidance</h3>
                    <p>{profile?.guidance}</p>
                  </div>
                </>
              )}

              <div className="report-actions">
                <button className="try-again-btn" onClick={handleTryAgain}>
                  Try Again
                </button>
              </div>

            </div>
          )}

        </div>

        {/* RIGHT SIDEBAR */}
        <div className="right-numerology">
          <h4>Related Links</h4>
          <div className="right-links">
            <Link to="/numerology/luckynum"><p><img src={arrow}/> Lucky Number</p></Link>
            <Link to="/numerology/chinese-lucky"><p><img src={arrow}/> Chinese Lucky</p></Link>
            <Link to="/numerology/unlucky"><p><img src={arrow}/> Unlucky Number</p></Link>
            <Link to="/numerology/life-path"><p><img src={arrow}/> Life Path</p></Link>
            <Link to="/numerology/destiny"><p><img src={arrow}/> Destiny Number</p></Link>
            <Link to="/numerology/birth"><p><img src={arrow}/> Birth or Root Number</p></Link>
            <Link to="/numerology/soul"><p><img src={arrow}/> Soul Urge Number</p></Link>
            <Link to="/numerology/name"><p><img src={arrow}/> Name Number</p></Link>
            <Link to="/numerology/balance"><p><img src={arrow}/> Balance Number</p></Link>
            <Link to="/numerology/lo-shu-grid"><p><img src={arrow}/> Lo Shu Grid Analysis</p></Link>
            <Link to="/numerology/kua"><p><img src={arrow}/> Kua Number</p></Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LuckyNum;
